import { FiBox, FiChevronLeft, FiChevronRight, FiEdit, FiFilter, FiPlus, FiSearch, FiTrash2, FiUsers } from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { addProduct, adjustStock, editProduct, removeProduct, } from '../../redux/slices/productSlice';
import { useDispatch, useSelector } from 'react-redux';

import Dropdown from '../../components/Dropdown';
import Papa from 'papaparse';
import { addCategory } from '../../redux/slices/categorySlice';
import { editRepresentative } from '../../redux/slices/representativesSlice';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const ProductsByCategory = () => {
  const { categoryName } = useParams();
  const dispatch = useDispatch();
  const allProducts = useSelector(state => state.products.products);
  const products = allProducts.filter(p => p.category === categoryName);
  const reps = useSelector(state => state.representatives.representatives);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', mainStock: '', lowStockThreshold: 10, category: categoryName });
  const [deleteId, setDeleteId] = useState(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockProduct, setStockProduct] = useState(null);
  const [stockAmount, setStockAmount] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignProduct, setAssignProduct] = useState(null);
  const [assignRepId, setAssignRepId] = useState('');
  const [assignQty, setAssignQty] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [stockStatus, setStockStatus] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  // const totalPages = Math.max(1, Math.ceil(products.length / pageSize));
  // const paginated = products.slice((page - 1) * pageSize, page * pageSize);
  const placeholderImg = 'https://via.placeholder.com/48x48?text=No+Image';
  const categories = useSelector(state => state.categories.categories);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const { t } = useTranslation();
  useEffect(() => {
    setForm(f => ({ ...f, category: categoryName }));
  }, [categoryName]);

  // Low stock notification logic
  useEffect(() => {
    // Use sessionStorage to track notified products
    const notified = new Set(JSON.parse(sessionStorage.getItem('lowStockNotified') || '[]'));
    products.forEach(product => {
      if (product.mainStock <= (product.lowStockThreshold ?? 10) && !notified.has(product.id)) {
        toast.warn(`Low stock: ${product.name} (${product.mainStock} left)`);
        notified.add(product.id);
      }
    });
    sessionStorage.setItem('lowStockNotified', JSON.stringify(Array.from(notified)));
    // Only run when products change
  }, [products]);

  // Filtering logic (matches Products page)
  const filteredProducts = products.filter(product => {
    const matchesSearch =
      !search ||
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      product.description.toLowerCase().includes(search.toLowerCase());
    const matchesPrice =
      (!priceRange.min || product.price >= Number(priceRange.min)) &&
      (!priceRange.max || product.price <= Number(priceRange.max));
    const matchesStockStatus =
      !stockStatus ||
      (stockStatus === 'low' && product.mainStock <= (product.lowStockThreshold ?? 10)) ||
      (stockStatus === 'out' && product.mainStock === 0) ||
      (stockStatus === 'in' && product.mainStock > (product.lowStockThreshold ?? 10));
    return matchesSearch && matchesPrice && matchesStockStatus;
  });

  // Sorting logic (matches Products page)
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (sortBy === 'price' || sortBy === 'mainStock') {
      aVal = Number(aVal);
      bVal = Number(bVal);
    }
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / pageSize));
  const paginated = sortedProducts.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [pageSize, totalPages, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, priceRange, stockStatus, sortBy, sortOrder]);

  // Handlers
  const openAddModal = () => {
    setForm({ name: '', description: '', price: '', mainStock: '', lowStockThreshold: 10, category: categoryName });
    setEditMode(false);
    setEditId(null);
    setShowModal(true);
  };
  const openEditModal = (product) => {
    setForm({ ...product });
    setEditMode(true);
    setEditId(product.id);
    setShowModal(true);
  };
  const handleFormChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name || !form.price || !form.mainStock) return;
    if (editMode) {
      dispatch(editProduct({ ...form, id: editId, price: Number(form.price), mainStock: Number(form.mainStock) }));
    } else {
      dispatch(addProduct({ ...form, id: 'prod-' + Date.now(), price: Number(form.price), mainStock: Number(form.mainStock) }));
    }
    setShowModal(false);
  };
  const handleDelete = id => setDeleteId(id);
  const confirmDelete = () => {
    dispatch(removeProduct(deleteId));
    setDeleteId(null);
  };
  const openStockModal = (product) => {
    setStockProduct(product);
    setStockAmount('');
    setShowStockModal(true);
  };
  const handleStockAdjust = () => {
    if (!stockProduct || !stockAmount) return;
    dispatch(adjustStock({ id: stockProduct.id, amount: Number(stockAmount) }));
    setShowStockModal(false);
    // Show toast if now low stock
    const newStock = Number(stockProduct.mainStock) + Number(stockAmount);
    if (newStock <= (stockProduct.lowStockThreshold ?? 10)) {
      toast.warn(`Low stock: ${stockProduct.name} (${newStock} left)`);
    }
  };
  // Assign stock to rep
  const openAssignModal = (product) => {
    setAssignProduct(product);
    setAssignRepId('');
    setAssignQty('');
    setShowAssignModal(true);
  };
  const handleAssignStock = () => {
    if (!assignProduct || !assignRepId || !assignQty) return alert('Not enough main stock!');
    // Deduct from main stock
    if (assignProduct.mainStock < Number(assignQty)) return alert('Not enough main stock!');
    dispatch(adjustStock({ id: assignProduct.id, amount: -Number(assignQty) }));
    // Add to rep stock
    const rep = reps.find(r => r.id === assignRepId);
    if (rep) {
      const newStock = Array.isArray(rep.stock) ? [...rep.stock] : [];
      const idx = newStock.findIndex(s => s.productId === assignProduct.id);
      if (idx !== -1) {
        newStock[idx].quantity += Number(assignQty);
      } else {
        newStock.push({ productId: assignProduct.id, quantity: Number(assignQty) });
      }
      dispatch(editRepresentative({ ...rep, stock: newStock }));
    }
    setShowAssignModal(false);
  };
  // CSV Export
  const handleExportCSV = () => {
    const csv = Papa.unparse(products.map(({ id, name, description, price, mainStock, lowStockThreshold, image }) => ({ id, name, description, price, mainStock, lowStockThreshold, image })));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'products.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Products exported as CSV!');
  };
  // CSV Import
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        if (!Array.isArray(results.data)) return toast.error('Invalid CSV format.');
        let imported = 0;
        results.data.forEach(row => {
          if (row.id && row.name) {
            dispatch(editProduct({
              id: row.id,
              name: row.name,
              description: row.description || '',
              price: Number(row.price) || 0,
              mainStock: Number(row.mainStock) || 0,
              lowStockThreshold: Number(row.lowStockThreshold) || 10,
              image: row.image || '',
            }));
            imported++;
          } else if (row.name) {
            dispatch(addProduct({
              id: 'prod-' + Date.now() + Math.random(),
              name: row.name,
              description: row.description || '',
              price: Number(row.price) || 0,
              mainStock: Number(row.mainStock) || 0,
              lowStockThreshold: Number(row.lowStockThreshold) || 10,
              image: row.image || '',
            }));
            imported++;
          }
        });
        toast.success(`Imported ${imported} products from CSV.`);
      },
      error: () => toast.error('Failed to parse CSV.'),
    });
  };
  // Download Template
  const handleDownloadTemplate = () => {
    const csv = Papa.unparse([
      { id: 'prod1', name: 'Sample Product', description: 'Description', price: 10, mainStock: 100, lowStockThreshold: 10, image: '' }
    ]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'products_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.info('Template downloaded.');
  };

  return (
    <div className="p-2 md:p-8 font-sans bg-gradient-to-br from-blue-50 via-white to-blue-100 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <h2 className="text-2xl font-extrabold flex items-center gap-2 text-blue-700">
          <FiBox className="w-6 h-6 text-blue-500" /> {t('productsInCategory')} <span className="text-blue-900">{categoryName}</span>
        </h2>
        <Link
          to="/admin/products"
          className="bg-blue-600 text-white rounded-full shadow-lg px-6 h-12 text-base font-semibold flex items-center gap-2 justify-center hover:bg-blue-700 transition"
        >
          {t('allProducts')}
        </Link>
      </div>
      {/* Action Buttons */}
      <div className="w-full flex flex-col sm:flex-row sm:flex-wrap gap-3 md:gap-4 mb-4">
        <button
          onClick={handleExportCSV}
          className="flex-1 sm:flex-none bg-blue-600 text-white rounded-full shadow-lg px-4 sm:px-6 min-w-[120px] sm:min-w-[150px] h-12 text-base font-semibold flex items-center gap-2 justify-center hover:bg-blue-700 transition"
        >
          {t('exportCSV')}
        </button>
        <label className="flex-1 sm:flex-none bg-blue-600 text-white rounded-full shadow-lg px-4 sm:px-6 min-w-[120px] sm:min-w-[150px] h-12 text-base font-semibold flex items-center gap-2 justify-center cursor-pointer hover:bg-blue-700 transition">
          {t('importCSV')}
          <input type="file" accept=".csv" onChange={handleImportCSV} className="hidden" />
        </label>
        <button
          onClick={handleDownloadTemplate}
          className="flex-1 sm:flex-none bg-blue-600 text-white rounded-full shadow-lg px-4 sm:px-6 min-w-[120px] sm:min-w-[150px] h-12 text-base font-semibold flex items-center gap-2 justify-center hover:bg-blue-700 transition"
        >
          {t('downloadTemplate')}
        </button>
        <button
          onClick={openAddModal}
          className="flex-1 sm:flex-none bg-blue-600 text-white rounded-full shadow-lg px-4 sm:px-6 min-w-[120px] sm:min-w-[150px] h-12 text-base font-semibold flex items-center gap-2 justify-center hover:bg-blue-700 transition"
        >
          <FiPlus /> {t('addProduct')}
        </button>
        <Link
          to="/admin/products/categories"
          className="flex-1 sm:flex-none bg-purple-600 text-white rounded-full shadow-lg px-4 sm:px-6 min-w-[120px] sm:min-w-[150px] h-12 text-base font-semibold flex items-center gap-2 justify-center hover:bg-purple-700 transition"
        >
          {t('manageCategories')}
        </Link>
      </div>
      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-4 mb-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
          <div className="bg-white rounded-full shadow-md px-4 py-3 focus-within:ring-4 focus-within:ring-blue-200 border-2 border-blue-100 flex items-center gap-2 flex-1">
            <FiSearch className="text-blue-400 text-lg" />
            <input
              type="text"
              placeholder={t('searchByNameOrDescription')}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="bg-transparent border-none outline-none flex-1 px-2 py-1 rounded-full text-base focus:ring-0"
            />
          </div>
          <Dropdown
            value={categoryName}
            onChange={() => {}} // category is fixed here, so disable
            options={[
              { value: categoryName, label: categoryName }
            ]}
            className="w-48"
            disabled
          />
          <button
            onClick={() => { setSearch(''); setPriceRange({ min: '', max: '' }); setStockStatus(''); setSortBy('name'); setSortOrder('asc'); setPage(1); }}
            className="border-2 border-blue-400 bg-white text-blue-700 rounded-full px-6 py-2 text-base font-semibold shadow hover:bg-blue-50 transition"
          >
            {t('reset')}
          </button>
          <button
            onClick={() => setShowFilters(f => !f)}
            className="flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-2 text-base font-semibold hover:bg-blue-200 transition"
          >
            <FiFilter className="w-5 h-5" />
            {t('filters')}
            {showFilters ? '−' : '+'}
          </button>
        </div>
      </div>
      {/* Filters Panel */}
      {showFilters && (
        <div className="mt-4 p-4 bg-white/90 rounded-2xl shadow-lg border border-blue-100 w-full flex flex-col overflow-visible max-w-full">
          <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 w-full">
            {/* Price Range */}
            <div className="min-w-0 w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('priceRange')}</label>
              <div className="flex flex-col xs:flex-row gap-2 w-full">
                <input
                  type="number"
                  placeholder={t('min')}
                  value={priceRange.min}
                  onChange={e => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                  className="flex-1 border border-gray-300 rounded-full px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 min-w-0"
                />
                <input
                  type="number"
                  placeholder={t('max')}
                  value={priceRange.max}
                  onChange={e => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                  className="flex-1 border border-gray-300 rounded-full px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 min-w-0"
                />
              </div>
            </div>
            {/* Stock Status */}
            <div className="min-w-0 w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('stockStatus')}</label>
              <Dropdown
                value={stockStatus}
                onChange={setStockStatus}
                options={[
                  { value: '', label: t('allStock') },
                  { value: 'low', label: t('lowStock') },
                  { value: 'out', label: t('outOfStock') },
                  { value: 'in', label: t('inStock') }
                ]}
                placeholder={t('selectStockStatus')}
                className="w-full"
              />
            </div>
            {/* Sort By */}
            <div className="min-w-0 w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('sortBy')}</label>
              <div className="flex flex-col xs:flex-row gap-2 w-full">
                <Dropdown
                  value={sortBy}
                  onChange={setSortBy}
                  options={[
                    { value: 'name', label: t('name') },
                    { value: 'price', label: t('price') },
                    { value: 'mainStock', label: t('stock') }
                  ]}
                  placeholder={t('selectSortBy')}
                  className="flex-1 min-w-0"
                />
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold hover:bg-blue-200 transition"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Table view for md+ screens */}
      <div className="overflow-x-auto w-full rounded-xl shadow-xl bg-white/90 ring-1 ring-blue-100 hidden md:block">
        <table className="min-w-full text-sm md:text-base">
          <thead className="bg-blue-50">
            <tr>
              <th className="px-4 py-2 text-left">{t('name')}</th>
              <th className="px-4 py-2 text-left">{t('description')}</th>
              <th className="px-4 py-2 text-left">{t('price')}</th>
              <th className="px-4 py-2 text-left">{t('mainStock')}</th>
              <th className="px-4 py-2 text-left">{t('lowStockThreshold')}</th>
              <th className="px-4 py-2 text-left">{t('image')}</th>
              <th className="px-4 py-2 text-left">{t('category')}</th>
              <th className="px-4 py-2 text-center">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-gray-400">{t('noProductsFound')}</td>
              </tr>
            ) : (
              paginated.map(product => (
                <tr key={product.id} className="border-b last:border-b-0 hover:bg-blue-50 transition">
                  <td className="px-4 py-2 font-semibold align-top">{product.name}</td>
                  <td className="px-4 py-2 align-top">{product.description}</td>
                  <td className="px-4 py-2 align-top">{product.price} SAR</td>
                  <td className="px-4 py-2 align-top">{product.mainStock}
                    {product.mainStock <= (product.lowStockThreshold ?? 10) && (
                      <span className="ml-2 px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-semibold">{t('low')}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 align-top">{product.lowStockThreshold ?? 10}</td>
                  <td className="px-4 py-2 align-top">
                    <img src={product.image || placeholderImg} alt={product.name} className="w-12 h-12 object-cover rounded border" />
                  </td>
                  <td className="px-4 py-2 align-top">{product.category || '-'}</td>
                  <td className="px-4 py-2 align-top">
                    <div className="flex flex-col items-center gap-2 mt-2">
                      <div className="flex flex-wrap gap-2 justify-center w-full">
                        <button className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold text-xs shadow hover:bg-yellow-200 transition min-w-[110px] h-10 flex items-center justify-center" onClick={() => openStockModal(product)}>{t('adjustStock')}</button>
                        <button className="bg-green-600 text-white px-3 py-1 rounded-full font-semibold text-xs shadow hover:bg-green-700 transition flex items-center gap-1 min-w-[110px] h-10 justify-center" onClick={() => openAssignModal(product)}><FiUsers className="inline" />{t('assignToRep')}</button>
                        <button className="bg-blue-600 text-white px-3 py-1 rounded-full font-semibold text-xs shadow hover:bg-blue-700 transition flex items-center gap-1 min-w-[110px] h-10 justify-center" onClick={() => openEditModal(product)}><FiEdit className="inline" />{t('edit')}</button>
                        <button className="bg-red-600 text-white px-3 py-1 rounded-full font-semibold text-xs shadow hover:bg-red-700 transition flex items-center gap-1 min-w-[110px] h-10 justify-center" onClick={() => handleDelete(product.id)}><FiTrash2 className="inline" />{t('delete')}</button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Card view for mobile */}
      <div className="block md:hidden w-full mt-4">
        {paginated.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center text-gray-400 text-center">
            <FiBox className="w-10 h-10 mb-2 text-blue-200" />
            <div className="font-semibold text-lg">{t('noProductsFound')}</div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {paginated.map(product => (
              <div key={product.id} className="bg-white rounded-2xl shadow-xl p-4 flex flex-col gap-2">
                <div className="flex items-center gap-3 mb-2">
                  <img src={product.image || placeholderImg} alt={product.name} className="w-14 h-14 object-cover rounded border bg-blue-50" />
                  <div className="flex-1">
                    <div className="font-bold text-blue-700 text-lg">{product.name}</div>
                    <div className="text-gray-500 text-sm">{product.description}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-sm mb-2">
                  <span className="bg-blue-50 text-blue-700 rounded-full px-3 py-1">{t('price')}: {product.price} SAR</span>
                  <span className="bg-blue-50 text-blue-700 rounded-full px-3 py-1">{t('mainStock')}: {product.mainStock}</span>
                  <span className="bg-blue-50 text-blue-700 rounded-full px-3 py-1">{t('lowStockThreshold')}: {product.lowStockThreshold ?? 10}</span>
                  {product.mainStock <= (product.lowStockThreshold ?? 10) && (
                    <span className="bg-red-100 text-red-700 rounded-full px-3 py-1 font-semibold">{t('low')}</span>
                  )}
                  <span className="bg-blue-50 text-blue-700 rounded-full px-3 py-1">{t('category')}: {product.category || '-'}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button className="bg-yellow-500 text-white px-4 py-2 rounded-full text-xs font-semibold shadow hover:bg-yellow-600 transition w-full min-w-[130px] h-10 flex items-center justify-center" onClick={() => openStockModal(product)}>{t('adjustStock')}</button>
                  <button className="bg-green-600 text-white px-4 py-2 rounded-full text-xs font-semibold shadow hover:bg-green-700 transition w-full flex items-center justify-center gap-1 min-w-[130px] h-10" onClick={() => openAssignModal(product)}><FiUsers className="inline" />{t('assignToRep')}</button>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-full text-xs font-semibold shadow hover:bg-blue-700 transition w-full flex items-center justify-center gap-1 min-w-[130px] h-10" onClick={() => openEditModal(product)}><FiEdit className="inline" />{t('edit')}</button>
                  <button className="bg-red-600 text-white px-4 py-2 rounded-full text-xs font-semibold shadow hover:bg-red-700 transition w-full flex items-center justify-center gap-1 min-w-[130px] h-10" onClick={() => handleDelete(product.id)}><FiTrash2 className="inline" />{t('delete')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Pagination Controls */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/90 rounded-2xl shadow-lg p-4 border border-blue-100">
        {/* Showing info */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-semibold">
            {t('showing')} {products.length === 0 ? 0 : ((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, products.length)} {t('of')} {products.length} {t('products')}
          </span>
        </div>
        {/* Pagination Controls */}
        <div className="flex flex-wrap items-center gap-1 sm:gap-2 justify-center">
          {/* First Page */}
          <button
            disabled={page === 1}
            onClick={() => setPage(1)}
            className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            title={t('firstPage')}
          >
            <FiChevronLeft className="w-4 h-4" />
            <FiChevronLeft className="w-4 h-4 -ml-3" />
          </button>
          {/* Previous Page */}
          <button
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            title={t('previousPage')}
          >
            <FiChevronLeft className="w-4 h-4" />
          </button>
          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              if (pageNum < 1 || pageNum > totalPages) return null;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded-full text-sm font-semibold transition flex items-center justify-center
                    ${page === pageNum
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  aria-current={page === pageNum ? 'page' : undefined}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          {/* Next Page */}
          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            title={t('nextPage')}
          >
            <FiChevronRight className="w-4 h-4" />
          </button>
          {/* Last Page */}
          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(totalPages)}
            className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            title={t('lastPage')}
          >
            <FiChevronRight className="w-4 h-4" />
            <FiChevronRight className="w-4 h-4 -ml-3" />
          </button>
        </div>
        {/* Page Size Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 font-semibold">{t('show')}:</span>
          <Dropdown
            value={pageSize.toString()}
            onChange={value => {
              setPageSize(Number(value));
              setPage(1);
            }}
            options={[
              { value: '10', label: '10' },
              { value: '20', label: '20' },
              { value: '50', label: '50' }
            ]}
            className="w-16"
          />
        </div>
      </div>
      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-md mx-2 relative max-h-[90vh] overflow-y-auto animate-fade-in">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-red-600 text-2xl font-bold" onClick={() => setShowModal(false)}>&times;</button>
            <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">{editMode ? t('editProduct') : t('addProduct')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-semibold mb-1">{t('name')}</label>
                <input type="text" name="name" value={form.name} onChange={handleFormChange} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">{t('description')}</label>
                <input type="text" name="description" value={form.description} onChange={handleFormChange} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">{t('priceSar')}</label>
                <input type="number" name="price" value={form.price} onChange={handleFormChange} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200" required min={0} />
              </div>
              <div>
                <label className="block font-semibold mb-1">{t('mainStock')}</label>
                <input type="number" name="mainStock" value={form.mainStock} onChange={handleFormChange} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200" required min={0} />
              </div>
              <div>
                <label className="block font-semibold mb-1">{t('lowStockThreshold')}</label>
                <input type="number" name="lowStockThreshold" value={form.lowStockThreshold ?? 10} onChange={handleFormChange} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200" required min={0} />
              </div>
              <div>
                <label className="block font-semibold mb-1">{t('image')}</label>
                <input type="file" accept="image/*" onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new window.FileReader();
                  reader.onload = ev => setForm(f => ({ ...f, image: ev.target.result }));
                  reader.readAsDataURL(file);
                }} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200" />
                {form.image && (
                  <img src={form.image} alt="Preview" className="w-16 h-16 object-cover rounded mt-2 border" />
                )}
              </div>
              <div>
                <label className="block font-semibold mb-1">{t('category')}</label>
                <div className="flex gap-2 items-center">
                  <Dropdown
                    value={form.category || ''}
                    onChange={val => setForm(f => ({ ...f, category: val }))}
                    options={[
                      { value: '', label: t('selectCategory') },
                      ...categories.map(c => ({ value: c.name, label: `${c.name} - ${c.description}` })),
                    ]}
                    placeholder={t('selectCategory')}
                    className="w-full"
                  />
                  <button type="button" onClick={() => setShowCategoryModal(true)} className="bg-blue-500 text-white rounded-full px-2 py-1 text-xs">+</button>
                </div>
                <a
                  href="/admin/products/categories"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-xs mt-1 inline-block"
                >
                  + {t('addOrManageCategories')}
                </a>
              </div>
              <button type="submit" className="bg-blue-600 text-white rounded-full shadow-lg px-6 py-2 text-base font-semibold flex items-center gap-2 hover:bg-blue-700">{editMode ? t('saveChanges') : t('addProduct')}</button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-xs mx-2 relative max-h-[90vh] overflow-y-auto animate-fade-in">
            <h3 className="text-lg font-bold mb-4 text-center text-red-700">{t('confirmDelete')}</h3>
            <p className="mb-6 text-center">{t('areYouSureDeleteProduct')}</p>
            <div className="flex gap-4 justify-center">
              <button className="bg-red-600 text-white rounded-full px-4 py-2 text-sm hover:bg-red-700" onClick={confirmDelete}>{t('delete')}</button>
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-400" onClick={() => setDeleteId(null)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {showStockModal && stockProduct && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-xs mx-2 relative max-h-[90vh] overflow-y-auto animate-fade-in">
            <h3 className="text-lg font-bold mb-4 text-center text-blue-700">{t('adjustStock')}</h3>
            <p className="mb-2 text-center">{t('product')}: <span className="font-semibold">{stockProduct.name}</span></p>
            <input type="number" value={stockAmount} onChange={e => setStockAmount(e.target.value)} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200 mb-4" placeholder={t('amountPlusMinus')} />
            <div className="flex gap-4 justify-center">
              <button className="bg-blue-600 text-white rounded-full px-4 py-2 text-sm hover:bg-blue-700" onClick={handleStockAdjust}>{t('adjust')}</button>
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-400" onClick={() => setShowStockModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Stock to Rep Modal */}
      {showAssignModal && assignProduct && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-xs mx-2 relative max-h-[90vh] overflow-y-auto animate-fade-in">
            <h3 className="text-lg font-bold mb-4 text-center text-green-700">{t('assignStockToRep')}</h3>
            <p className="mb-2 text-center">{t('product')}: <span className="font-semibold">{assignProduct.name}</span></p>
            <Dropdown
              value={assignRepId}
              onChange={setAssignRepId}
              options={[
                { value: '', label: t('selectRepresentative') },
                ...reps.map(r => ({ value: r.id, label: r.name })),
              ]}
              placeholder={t('selectRepresentative')}
              className="mb-2 w-full"
            />
            <input type="number" value={assignQty} onChange={e => setAssignQty(e.target.value)} className="border rounded-full px-2 py-2 w-full focus:ring-2 focus:ring-blue-200 mb-4" placeholder={t('quantity')} min={1} max={assignProduct.mainStock} />
            <div className="flex gap-4 justify-center">
              <button className="bg-green-600 text-white rounded-full px-4 py-2 text-sm hover:bg-green-700" onClick={handleAssignStock}>{t('assign')}</button>
              <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-400" onClick={() => setShowAssignModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">{t('addNewCategory')}</h3>
            <input
              type="text"
              placeholder={t('categoryName')}
              value={newCategory.name}
              onChange={e => setNewCategory(c => ({ ...c, name: e.target.value }))}
              className="border rounded-full px-3 py-2 w-full mb-2"
            />
            <textarea
              placeholder={t('description')}
              value={newCategory.description}
              onChange={e => setNewCategory(c => ({ ...c, description: e.target.value }))}
              className="border rounded-full px-3 py-2 w-full mb-4"
              rows={2}
            />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowCategoryModal(false)} className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300">{t('cancel')}</button>
              <button type="button" onClick={() => {
                if (!newCategory.name) return;
                dispatch(addCategory(newCategory));
                setForm(f => ({ ...f, category: newCategory.name }));
                setShowCategoryModal(false);
                setNewCategory({ name: '', description: '' });
              }} className="px-4 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700">{t('add')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsByCategory;